/* Dialog behavior only. Shopify's native customer form owns subscription and success. */
(() => {
  'use strict';
  if (window.KaliaWelcomeOffer) return;
  const instances = new WeakMap();
  class KaliaWelcomeOffer {
    constructor(root,options = {}) {
      this.root = root;
      this.dialog = root.querySelector('[data-offer-dialog]');
      this.launcher = root.querySelector('[data-offer-open]');
      if (!this.dialog || !this.launcher || typeof this.dialog.showModal !== 'function') return;
      this.now = options.now || (() => Date.now());
      this.controller = new AbortController();
      this.listeners = {signal:this.controller.signal};
      this.editor = Boolean(window.Shopify?.designMode);
      const supplied = root.dataset.campaignId || '';
      const campaign = /^[A-Za-z0-9_-]{1,80}$/.test(supplied) ? supplied : 'welcome-v1';
      this.key = `kalia.welcome.v1:${campaign}`;
      const days = Number(root.dataset.repeatDays), delay = Number(root.dataset.delaySeconds);
      this.repeatDays = Number.isInteger(days) && days >= 0 && days <= 30 ? days : 7;
      this.delay = Number.isFinite(delay) && delay >= 5 && delay <= 60 ? delay * 1000 : 15000;
      this.launcher.hidden = false;
      this.launcher.addEventListener('click',() => this.open('manual'),this.listeners);
      root.querySelector('[data-offer-close]')?.addEventListener('click',() => this.close('dismiss'),this.listeners);
      this.dialog.addEventListener('cancel',event => { event.preventDefault(); this.close('dismiss'); },this.listeners);
      this.dialog.addEventListener('close',() => this.finishClose(this.closeReason || 'dismiss'),this.listeners);
      this.dialog.addEventListener('click',event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close('dismiss');
      },this.listeners);
      root.querySelector('[data-offer-copy]')?.addEventListener('click',() => this.copyCode(),this.listeners);
      document.addEventListener('shopify:section:select',event => {
        if (this.editor && String(event.detail?.sectionId) === root.dataset.sectionId) this.open('editor');
      },this.listeners);
      document.addEventListener('shopify:section:deselect',event => {
        if (this.editor && String(event.detail?.sectionId) === root.dataset.sectionId) this.close('editor');
      },this.listeners);
      if (root.querySelector('[data-offer-success]')) {
        this.write('localStorage',`${this.key}:complete`,'1');
        this.open('server');
      } else if (root.querySelector('[data-offer-error]')) this.open('server');
      else if (this.autoEligible()) this.timer = setTimeout(() => {
        this.timer = null;
        if (this.autoEligible() && !this.isBusy()) this.open('auto');
      },this.delay);
    }
    read(storage,key) {
      try { return window[storage].getItem(key); } catch (_) { return null; }
    }
    write(storage,key,value) {
      if (this.editor) return;
      try { window[storage].setItem(key,value); } catch (_) { /* The dialog still works without optional display storage. */ }
    }
    autoEligible() {
      return !this.destroyed && !this.editor && !this.opened && !this.seen &&
        this.root.dataset.autoEnable === 'true' && ['index','collection'].includes(this.root.dataset.pageType) &&
        this.root.dataset.subscribed !== 'true' && this.read('localStorage',`${this.key}:complete`) !== '1' &&
        this.read('sessionStorage',`${this.key}:shown`) !== '1' &&
        !(Number(this.read('localStorage',`${this.key}:until`)) > this.now());
    }
    otherDialogOpen() {
      return [...document.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"]')].some(element => {
        if (element === this.dialog || element.hidden || !element.getClientRects().length) return false;
        const style = window.getComputedStyle(element), rect = element.getBoundingClientRect();
        return style.display !== 'none' && style.visibility !== 'hidden' && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
      });
    }
    isBusy() {
      const active = document.activeElement;
      return document.visibilityState !== 'visible' || this.otherDialogOpen() ||
        Boolean(document.querySelector('details[data-kn-menu][open],cart-drawer.active,body[data-kalia-support-open="true"]')) ||
        Boolean(active?.matches?.('input,textarea,select,iframe,[contenteditable="true"],[role="textbox"]') || active?.closest?.('form'));
    }
    open(reason) {
      if (this.destroyed || this.dialog.open || (reason !== 'server' && reason !== 'editor' && this.otherDialogOpen())) return false;
      if (reason === 'auto' && this.isBusy()) return false;
      clearTimeout(this.timer); this.timer = null;
      this.previousFocus = document.activeElement;
      try { this.dialog.showModal(); } catch (_) { return false; }
      this.opened = true; this.closeReason = '';
      this.seen = true;
      if (reason !== 'editor') this.write('sessionStorage',`${this.key}:shown`,'1');
      this.ownsLock = !document.documentElement.classList.contains('kwo-modal-open');
      if (this.ownsLock) document.documentElement.classList.add('kwo-modal-open');
      const focus = this.root.querySelector('[data-offer-error]') || this.root.querySelector('[data-offer-success-title]') || this.root.querySelector('[data-offer-close]');
      focus?.focus({preventScroll:true});
      return true;
    }
    close(reason = 'dismiss') {
      if (!this.opened && !this.dialog.open) return;
      this.closeReason = reason;
      if (this.dialog.open) this.dialog.close();
      this.finishClose(reason);
    }
    finishClose(reason) {
      if (!this.opened) return;
      this.opened = false;
      if (reason === 'dismiss' && this.repeatDays > 0) this.write('localStorage',`${this.key}:until`,String(this.now() + this.repeatDays * 86400000));
      if (this.ownsLock) document.documentElement.classList.remove('kwo-modal-open');
      this.ownsLock = false;
      if (!this.destroyed && this.previousFocus?.isConnected) this.previousFocus.focus({preventScroll:true});
    }
    async copyCode() {
      const code = this.root.querySelector('[data-offer-success] [data-offer-code]');
      const status = this.root.querySelector('[data-offer-copy-status]');
      if (!code || !status) return;
      try {
        if (typeof window.navigator?.clipboard?.writeText !== 'function') throw new Error('Clipboard unavailable.');
        await window.navigator.clipboard.writeText(code.textContent.trim());
        status.textContent = 'Code copied.';
      } catch (_) { status.textContent = 'Select and copy the code above.'; }
    }
    destroy() {
      this.destroyed = true; clearTimeout(this.timer); this.timer = null;
      this.close('cleanup'); this.controller?.abort();
    }
  }
  window.KaliaWelcomeOffer = KaliaWelcomeOffer;
  const roots = target => [...(target.matches?.('[data-welcome-offer]') ? [target] : []), ...target.querySelectorAll('[data-welcome-offer]')];
  const start = target => roots(target).forEach(root => {
    if (!instances.has(root)) instances.set(root,new KaliaWelcomeOffer(root));
  });
  document.addEventListener('shopify:section:load',event => start(event.target));
  document.addEventListener('shopify:section:unload',event => roots(event.target).forEach(root => { instances.get(root)?.destroy(); instances.delete(root); }));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => start(document),{once:true}); else start(document);
})();
